import type { ReactNode } from "react";
import type { SessionUser } from "@ziolo/shared";
import { Navbar } from "./Navbar.js";
import { UserBar } from "./UserBar.js";
import { Celebration } from "./Celebration.js";
import { CookieBanner } from "./CookieBanner.js";
import { useSpecialTime } from "../hooks/useSpecialTime.js";

export interface SiteShellProps {
  user?: SessionUser | null | undefined;
  onLogout?: (() => void) | undefined;
  /** Full-width block above the columns (auth forms, page titles). */
  header?: ReactNode;
  /** Rendered in the sticky right column. Omit for a single-column page. */
  sidebar?: ReactNode;
  /** Admin panel link, shown in the navbar and session strip. */
  adminHref?: string | undefined;
  /** Extra footer links, e.g. Zgłoś buga. */
  footerExtra?: ReactNode;
  children: ReactNode;
}

/**
 * The frame every page and every migrated game sits in: navbar, optional sidebar,
 * cookie notice, and the 21:37 easter egg. Games get the site's chrome for free
 * instead of each one reimplementing a header.
 */
export function SiteShell({
  user,
  onLogout,
  header,
  sidebar,
  adminHref,
  footerExtra,
  children,
}: SiteShellProps) {
  const celebrating = useSpecialTime();

  return (
    <div className="z-shell">
      <Navbar adminHref={adminHref} />

      <div className="z-page">
        {user ? <UserBar user={user} onLogout={onLogout} adminHref={adminHref} /> : null}
        {header}

        <div className={`z-columns ${sidebar ? "" : "z-columns--single"}`}>
          <main className="z-main">{children}</main>
          {sidebar ? <aside className="z-sidebar">{sidebar}</aside> : null}
        </div>
      </div>

      <footer className="z-footer">
        <a href="/polityka-cookies">Polityka cookies</a>
        {footerExtra ? (
          <>
            <span className="z-footer__sep" aria-hidden="true">
              ·
            </span>
            {footerExtra}
          </>
        ) : null}
      </footer>

      <CookieBanner />
      {celebrating ? <Celebration /> : null}
    </div>
  );
}
